import { rgba } from 'polished';
import { ComponentProps, FunctionComponent, useRef } from 'react';
import { ForwardedRef, PropsWithChildren, ReactNode, forwardRef } from 'react';
import { Box, BoxProps, BoxType } from 'reflexbox';
import styled from 'styled-components';
import { useIntersectionObserver } from 'usehooks-ts';

import { PAGE_CONTENT_WIDTH, tokens } from '../theme/tokens';
import { Separator } from './Separator';

type ContainerBaseProps = { wide?: number | boolean; height?: string };
type ContainerWithoutRefProps = PropsWithChildren<BoxProps & ContainerBaseProps>;

function ContainerWithoutRef(props: ContainerWithoutRefProps, ref: ForwardedRef<HTMLDivElement>) {
	return (
		<Box
			ref={ref}
			px={props.wide ? 4 : [3, 4, 5]}
			{...(props as ComponentProps<BoxType>)}
			height={props.height}
		>
			<Box
				maxWidth={
					props.wide
						? typeof props.wide === 'number'
							? props.wide
							: undefined
						: PAGE_CONTENT_WIDTH
				}
				width="100%"
				marginX="auto"
				height={props.height}
			>
				{props.children}
			</Box>
		</Box>
	);
}

export const Container = forwardRef(
	ContainerWithoutRef,
) as unknown as FunctionComponent<ContainerWithoutRefProps> & { Sticky: typeof ContainerSticky };

const StickyBorder = styled(Box)`
	transition: 1s cubic-bezier(0, 1, 0, 1) opacity;
	opacity: 0;
`;

const Shadow = styled(Box)`
	position: absolute;
	top: 100%;
	width: 100%;
	height: 8px;
	background: linear-gradient(
		${rgba(tokens.border.subtle, 0.5)} 0%,
		${rgba(tokens.border.subtle, 0.25)} 25%,
		${rgba(tokens.border.subtle, 0.0)} 100%
	);
`;

const Sticky = styled(Box)<{ floating?: boolean }>`
	position: sticky;
	top: 0;
	background: white;
	z-index: 1;

	${StickyBorder} {
		opacity: ${(props) => (props.floating ? 1 : 0)};
	}
`;

type ContainerStickyProps = PropsWithChildren<{
	py?: number;
	header?: ReactNode;
}>;

function ContainerSticky(props: ContainerStickyProps) {
	const ref = useRef<HTMLDivElement>(null);

	const observer = useIntersectionObserver(ref, {
		threshold: 1,
	});

	return (
		<Box ref={ref}>
			{props.header && (
				<Sticky floating={observer?.isIntersecting === false}>
					<Container py={props.py}>{props.header}</Container>
					<StickyBorder>
						<Separator />
						<Shadow />
					</StickyBorder>
				</Sticky>
			)}
			<Container pb={props.py}>{props.children}</Container>
		</Box>
	);
}

Container.Sticky = ContainerSticky;
